import { AlertDialog } from "radix-ui";
import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from "react";
import { useT } from "@/lib/i18n";
import { Button } from "./button";

type ConfirmOptions = { title?: ReactNode; description?: ReactNode; confirmLabel?: ReactNode; cancelLabel?: ReactNode; danger?: boolean };
type ConfirmFn = (o?: ConfirmOptions) => Promise<boolean>;

const Ctx = createContext<ConfirmFn>(async () => window.confirm("?"));

/** `const confirm = useConfirm(); if (await confirm({ danger: true })) …` — an accessible alert dialog, focus returns to the trigger. */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const { t } = useT();
  const [state, setState] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<((v: boolean) => void) | null>(null);
  const confirm = useCallback<ConfirmFn>((o) => {
    setState(o ?? {});
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);
  const close = (v: boolean) => {
    resolver.current?.(v);
    resolver.current = null;
    setState(null);
  };
  return (
    <Ctx.Provider value={confirm}>
      {children}
      <AlertDialog.Root open={!!state} onOpenChange={(o) => !o && close(false)}>
        <AlertDialog.Portal>
          <AlertDialog.Overlay className="fixed inset-0 z-[80] bg-[var(--overlay)] backdrop-blur-[2px] animate-fade-in" />
          <AlertDialog.Content className="fixed start-1/2 top-1/2 z-[80] w-[calc(100vw-2rem)] max-w-sm -translate-y-1/2 rounded-3xl border border-line bg-surface p-6 shadow-pop ltr:-translate-x-1/2 rtl:translate-x-1/2 animate-rise">
            <AlertDialog.Title className="text-[16px] font-semibold text-ink">{state?.title ?? t("common.confirm.title")}</AlertDialog.Title>
            <AlertDialog.Description className="mt-2 text-[13.5px] leading-relaxed text-ink-2">{state?.description ?? (state?.danger ? t("common.confirm.irreversible") : "")}</AlertDialog.Description>
            <div className="mt-6 flex justify-end gap-2">
              <AlertDialog.Cancel asChild>
                <Button variant="ghost">{state?.cancelLabel ?? t("common.actions.cancel")}</Button>
              </AlertDialog.Cancel>
              <AlertDialog.Action asChild>
                <Button variant={state?.danger ? "danger-solid" : "primary"} onClick={() => close(true)}>
                  {state?.confirmLabel ?? t("common.actions.confirm")}
                </Button>
              </AlertDialog.Action>
            </div>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>
    </Ctx.Provider>
  );
}

export const useConfirm = () => useContext(Ctx);
